import React from "react";
import { styled } from "@mui/material/styles";
import {
  Grid,
  TextField,
  Typography,
  Paper,
  Box,
  FormControlLabel,
  Checkbox,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
} from "@mui/material";

const BRAND = "#f05d32";

const sectionPaperSx = {
  px: { xs: 2, md: 2.5 },
  py: { xs: 2, md: 2.5 },
  borderRadius: "16px",
  backgroundColor: "#fff",
  border: "1px solid rgba(0,0,0,0.06)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
};

const MyTextField = styled(TextField)(() => ({
  marginTop: "8px",
  marginBottom: "8px",
  "& label.Mui-focused": {
    color: BRAND,
  },
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff",
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.12)",
      borderRadius: "12px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(240, 93, 50, 0.45)",
    },
    "&.Mui-focused fieldset": {
      borderColor: BRAND,
      borderWidth: 1.5,
    },
  },
}));

const MyFormControl = styled(FormControl)(() => ({
  margin: "8px 0px",
  "& label.Mui-focused": {
    color: BRAND,
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.12)",
      borderRadius: "12px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(240, 93, 50, 0.45)",
    },
    "&.Mui-focused fieldset": {
      borderColor: BRAND,
      borderWidth: 1.5,
    },
  },
}));

const MetaRow = ({ icon, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.25,
      color: "#6b6b65",
      fontSize: 15,
      lineHeight: 1.45,
      mb: 1.25,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        bgcolor: "rgba(240, 93, 50, 0.1)",
        color: BRAND,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: 13,
      }}
    >
      <i className={icon} />
    </Box>
    <Box sx={{ pt: 0.5, minWidth: 0, fontWeight: 500, color: "#1a1a1a" }}>
      {children}
    </Box>
  </Box>
);

export default function Checkout(props) {
  const { component } = props;
  const devHelper = component.getHelpers("dev");

  const {
    order = null,
    cutlery = null,
    hours,
  } = component.getData("default", {});

  const delivery_type = devHelper.getObjectValue(order, "delivery_type");

  var type = "shipping_address";

  const changeValue = (value, attr) => {
    component.setData({
      ["default.order." + type + "." + attr]: value,
      ["default.address." + attr]: value,
    });
    component.deleteData("error." + type + "." + attr);
  };

  return (
    <>
      {order && (
        <Paper elevation={0} sx={sectionPaperSx}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: 18, sm: 20, md: 22 },
              lineHeight: 1.2,
              mb: 2,
              color: "#1a1a1a",
            }}
          >
            {component.ucfirst("checkout-title-1")}
          </Typography>

          <Box
            sx={{
              mb: 2.5,
              p: 1.75,
              borderRadius: "12px",
              bgcolor: "#fafafa",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <MetaRow
              icon={`fa-light fa-${delivery_type === "delivery" ? "moped" : "store"}`}
            >
              {component.ucfirst(delivery_type)}
            </MetaRow>

            {delivery_type === "delivery" ? (
              <MetaRow icon="fa-light fa-location-dot">
                {devHelper.getObjectValue(order, "shipping_address.address")}
              </MetaRow>
            ) : (
              <>
                <MetaRow icon="fa-light fa-location-dot">
                  {component.trans(
                    `${devHelper.getObjectValue(order, "spot.slug")}-address`,
                  )}
                </MetaRow>
                <MetaRow icon="fa-light fa-phone">
                  {component.trans(
                    `${devHelper.getObjectValue(order, "spot.phone")}`,
                  )}
                </MetaRow>
              </>
            )}
          </Box>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {!!hours?.length && (
              <Grid item xs={12} sm={6}>
                <MyFormControl fullWidth sx={{ mt: "8px", mb: "8px" }}>
                  <InputLabel>
                    <i className="fa-light fa-clock" />{" "}
                    {component.ucfirst("time")}
                  </InputLabel>
                  <Select
                    value={devHelper.getObjectValue(order, "time", "now")}
                    label={
                      <>
                        <i className="fa-light fa-clock" />{" "}
                        {component.ucfirst("time")}
                      </>
                    }
                    onChange={(e) => {
                      component.updateTime(e.target.value);
                    }}
                  >
                    <MenuItem value="now">{component.ucfirst("now")}</MenuItem>
                    {hours.map((hour, index) => (
                      <MenuItem
                        key={`hour-${index}`}
                        value={`${hour.hour}:${hour.min}`}
                      >
                        {`${hour.hour % 12}:${hour.min} `}
                        {component.trans(hour.hour < 12 ? "am" : "pm")}
                      </MenuItem>
                    ))}
                  </Select>
                </MyFormControl>
              </Grid>
            )}

            {delivery_type === "delivery" ? (
              <>
                <Grid item xs={12} sm={6}>
                  <MyTextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    label={component.ucfirst("phone-textfield")}
                    name="phone"
                    helperText={
                      component.getData("error." + type + ".phone", [
                        { message: "" },
                      ])[0].message
                    }
                    value={component.getData("default.address.phone", "")}
                    onChange={(e) => changeValue(e.target.value, "phone")}
                    error={component.dataExists("error." + type + ".phone")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MyTextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    label={component.ucfirst("bell-textfield")}
                    name="bell"
                    helperText={
                      component.getData("error." + type + ".bell", [
                        { message: "" },
                      ])[0].message
                    }
                    value={component.getData("default.address.bell", "")}
                    onChange={(e) => changeValue(e.target.value, "bell")}
                    error={component.dataExists("error." + type + ".bell")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MyTextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    label={component.ucfirst("floor-textfield")}
                    name="floor"
                    helperText={
                      component.getData("error." + type + ".floor", [
                        { message: "" },
                      ])[0].message
                    }
                    value={component.getData("default.address.floor", "")}
                    onChange={(e) => changeValue(e.target.value, "floor")}
                    error={component.dataExists("error." + type + ".floor")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MyTextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    label={component.ucfirst("notes-textfield")}
                    name="notes"
                    helperText={
                      component.getData("error." + type + ".notes", [
                        { message: "" },
                      ])[0].message
                    }
                    value={component.getData("default.address.notes", "")}
                    onChange={(e) => changeValue(e.target.value, "notes")}
                    error={component.dataExists("error." + type + ".notes")}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={hours?.length ? 6 : 12}>
                <MyTextField
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  label={component.ucfirst("phone-textfield")}
                  name="phone"
                  helperText={
                    component.getData("error.user.phone", [{ message: "" }])[0]
                      .message
                  }
                  value={component.getData("default.user.phone", "")}
                  onChange={(e) =>
                    component.setData({ "default.user.phone": e.target.value })
                  }
                  error={component.dataExists("error.user.phone")}
                />
              </Grid>
            )}
          </Grid>

          {cutlery !== null && (
            <FormControlLabel
              sx={{
                mt: 1,
                mr: 0,
                px: 1.5,
                py: 0.75,
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.06)",
                bgcolor: "#fafafa",
                width: "100%",
                ml: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: 16,
                },
              }}
              key={devHelper.getObjectValue(cutlery, "slug")}
              value={devHelper.getObjectValue(cutlery, "id")}
              control={
                <Checkbox
                  sx={{
                    color: BRAND,
                    "&.Mui-checked": {
                      color: BRAND,
                    },
                    "& .MuiSvgIcon-root": { fontSize: 20 },
                  }}
                  checked={devHelper.getObjectValue(cutlery, "selected")}
                  onChange={() => {
                    component.updateService(
                      devHelper.getObjectValue(cutlery, "id"),
                    );
                  }}
                />
              }
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: 15,
                    fontWeight: 500,
                    gap: 1,
                  }}
                >
                  {component.ucfirst(devHelper.getObjectValue(cutlery, "slug"))}
                  <i
                    className="fa-solid fa-utensils"
                    style={{ color: BRAND }}
                  />
                </Box>
              }
            />
          )}
        </Paper>
      )}
    </>
  );
}
